import { GridDate } from '@gelatoas/design-editor-calendar';

import { AddonType } from 'editor/src/store/editorModules/addons/types';
import type { LayoutGeneratedTextType } from 'editor/src/store/editorModules/layouts/types';
import { ExportedWarning } from 'editor/src/store/editorModules/warnings/types';
import { TextAlign, TextDirection } from 'editor/src/store/fonts/types';
import { MockupRole, MockupVariant } from 'editor/src/store/mockup/types';
import { PluginName } from 'editor/src/store/plugins/types';

export const IMAGE_CORRUPTED = 'IMAGE_CORRUPTED';

export enum ColorSpace {
  sRGB = 'rgb',
  CMYK = 'cmyk',
}

export interface DesignState {
  originalDesignData?: DesignData;
  designData?: DesignData;
  innerPagesCount?: number;
  spreadBackgrounds?: SpreadGroundImage[];
  spreadMasks?: SpreadGroundImage[];
  spreadForegrounds?: SpreadGroundImage[];
  autoApplyLayoutSchemaNames?: string[];
  designDataTouched: boolean;
  digitizedAssets: {
    [id: string]: DigitizedAsset;
  };
  globalPlugins: string[];
  designSavingStatus: SavingDesignStatus;
}

export type DigitizedAsset = DigitizedAssetImage | DigitizedAssetAddon | DigitizedAssetText;
export type DigitizedElementType = DigitizedAsset['elementType'];

export interface DigitizedAssetBase {
  url: string;
  imageId: string;
  stitchCount: number;
}

export interface DigitizedAssetImage extends DigitizedAssetBase {
  elementType: 'image';
  payload: DigitizeImagePayload;
}

export interface DigitizedAssetAddon extends DigitizedAssetBase {
  elementType: 'addon';
  payload: DigitizeImagePayload;
}

export interface DigitizedAssetText extends DigitizedAssetBase {
  elementType: 'text';
  margin: number;
  payload: DigitizeTextPayload;
}

export type DigitizedAssetFromType<T extends DigitizedAsset['elementType']> = T extends 'text'
  ? DigitizedAssetText
  : T extends 'image'
    ? DigitizedAssetImage
    : T extends 'addon'
      ? DigitizedAssetImage
      : never;

export interface DigitizeImagePayload {
  imageUrl: string;
  rendered_mm_width: number;
  output_px_width: number;
  elementUuid: number;
  variantKey: string | undefined;
  palette_override?: string[]; // Array of final thread colors for embroidery preview
}

export interface DigitizeTextPayload {
  textElement: MediaText;
  path: string;
  output_px_width: number;
  elementUuid: number;
  variantKey: string | undefined;
}

export interface DesignDimensions {
  width: number;
  height: number;
  min_width: number;
  min_height: number;
  max_height: number;
  max_surface: number | undefined;
  max_width?: number;
}

export interface DimensionRestrictions {
  min_width: number;
  min_height: number;
  max_width: number;
  max_height: number;
}

export interface SpreadGroundImage {
  name: string;
  url: string;
  width: number;
  height: number;
  thumbnailUrl: string;
  thumbnailWidth: number;
  thumbnailHeight: number;
  top: number;
  left: number;
  imgWidth: number;
  imgHeight: number;
}

export interface DesignDataMetadata {
  created_at: string;
  updated_at: string;
  edit_session_count: number;
  edit_duration_seconds: number;
}

export interface CalendarEvent {
  id: string;
  day: number;
  month: number;
  year: number;
  label: string;
  checked: boolean;
  custom: boolean;
  merchant: boolean;
  /** Public holidays only: the selected countries that observe this date. */
  countries?: string[];
}

export interface CalendarConfig {
  events?: CalendarEvent[];
  /** Countries whose public holidays the design carries, in the order they were picked. */
  countries?: string[];
  /** The customer picked the countries, so their location no longer replaces them. */
  countriesChosenByCustomer?: boolean;
  locale?: string;
  showEvents?: boolean;
  allowPersonalization?: boolean;
  autoApplyPublicHolidays?: boolean;
}

export interface DesignData {
  product_uid: string;
  spreads: Spread[];
  page_count?: number;
  page_count_limit?: PageCountLimit;
  design_family_id?: string | number;
  design_template_id?: string | number;
  plugins?: string[];
  dimensions?: DesignDimensions;
  related_dimensions?: DesignDimensions;
  spread_groups?: SpreadGroup[];
  resolution?: {
    dpi: DpiLevels;
  };
  personalizationSettings?: PersonalizationSettings;
  lifecycle_metadata?: DesignDataMetadata;
  color_space?: ColorSpace;
  calendar?: CalendarConfig;
  auto_zoom?: boolean;
  /** Variable data printing: the code set this design draws one
   *  row from per printed unit. Element bindings name a column of it. */
  variableData?: VariableDataBinding;
  /** GAPI-61895: named document text styles (Heading, Subtitle, Body...). A text
   *  joins one through `extra.textStyleId`; editing the style rewrites every
   *  member in one step. Absent on designs that never used them. */
  textStyles?: TextStyle[];
}

/**
 * A named text style shared by every text that references it.
 *
 * The style's values are also written onto each member text, so the stored
 * design renders and prints exactly as before without anything knowing about
 * styles: the reference only says which texts to rewrite when the style
 * changes. Paragraph spacing is not part of it yet, because the text engine
 * (and the renderers) have no paragraph spacing to drive.
 */
export interface TextStyle {
  id: string;
  name: string;
  fontFamily: string;
  /** Points, like `MediaText.extra.fontSize`. */
  fontSize: number;
  /** Text fill. Unset: the style leaves each member's own colour alone. */
  color?: string;
  /** Unset: the style leaves each member's own line height alone. */
  lineHeight?: number;
  /** Unset: the style leaves each member's own alignment alone (as do the ones below). */
  textAlign?: TextAlign;
  underline?: boolean;
  /** Strikethrough. */
  linethrough?: boolean;
  /** Same unit as `MediaText.extra.letterSpacing` (thousandths of an em). */
  letterSpacing?: number;
}

/** Where the rows come from at order time. A store bank is reserved from
 *  across orders and tracked for exhaustion; an order batch is the buyer's own
 *  upload, consumed once and never shared. */
export type VariableDataScope = 'storeBank' | 'orderBatch';

export interface VariableDataBinding {
  scope: VariableDataScope;
  /** The W2P code set id for a store bank. Absent for an order batch, which
   *  has no identity until the buyer uploads it. */
  codeSetId?: string;
  /** Human name shown wherever the binding is summarised. */
  codeSetName?: string;
  columns: string[];
  /** The one row every surface renders before real rows exist: the canvas,
   *  previews and the buyer's product page all show it. Production replaces it
   *  per unit. Synthetic by contract — it is saved and exported with the design
   *  and visible to buyers, so it must never hold an issuable code. */
  sampleRow: Record<string, string>;
}

export interface PersonalizationSettings {
  allowAddElements?: true;
  allowRemoveElements?: true;
  allowChangeBackgroundColor?: true;
  allowPrintFileProcessing?: true;
  allowChooseLayouts?: true;
  /** GAPI-61790: the customer's font picker offers only the tenant's uploaded fonts, with no
   *  bundled fonts and no font upload. See isOnlyUploadedFonts. */
  onlyUploadedFonts?: true;
}

interface DpiLevels {
  min: number;
  ok: number;
  max: number;
}

export interface ExportedDesignData extends DesignData {
  warnings?: ExportedWarning[];
  is_personalizable?: true;
  /** At least one element prints a code-set column, so production must
   *  materialise one design per unit rather than print this one N times. */
  is_variable_data?: true;
}

export type ConditionChild = { type: 'condition'; id: string } | { type: MediaElement['type']; id: number };
export type ConditionAddress = { spreadIndex: number; conditionId: string };
export enum ConditionDisplayOption {
  Button = 'button',
  Dropdown = 'dropdown',
  Thumbnail = 'thumbnail',
}

export interface Condition {
  id: string;
  parent: { conditionId: string; optionIds: string[] } | undefined;
  name: string;
  /** Author-written notice shown to the buyer under the condition's label, as
   *  standing text rather than a tooltip. For statements the buyer must read
   *  before choosing — "this is not a translation tool" on a language
   *  picklist — where hover-to-discover is the wrong affordance. */
  description?: string;
  /** Design-scoped picklist: the buyer sees this condition on every page, not
   *  only on the spread that stores it. Selection still dispatches against the
   *  owning spread. Pairs with `TextConditionVariants` so one dropdown can
   *  drive label swaps across both sides of a card. */
  showOnAllSpreads?: true;
  displayOption: ConditionDisplayOption;
  options: ConditionOption[];
  activeOptionId: string;
}

export interface ConditionOption {
  id: string;
  label: string;
  imageId: string | undefined;
}

export const conditionGroupChildenKey = 'top';

export interface PersonalizationFieldGroup {
  id: string;
  name: string;
  childrenKey: string;
  elements: ConditionChild[];
}

export interface ConditionGroup {
  fieldGroups?: PersonalizationFieldGroup[];
  conditions: { [conditionId: string]: Condition };
  children: { [id: string]: ConditionChild[] }; // conditionGroupChildenKey | conditionId + optionId
}

export enum PrintTechnology {
  DTG = 'DTG',
  DTF = 'DTF',
  Embroidery = 'Embroidery',
}

export interface ReflowRow {
  id: string;
  optional: boolean;
  elementUuids: number[];
  /**
   * Current applied y-shift for this row (in mm). Positive = down, negative = up.
   * When a previous row collapses, this row's shiftY becomes negative and its
   * elements' `y` values are mutated by the same delta. When that row reopens,
   * shiftY returns toward 0 and the elements move back. This lets us re-derive
   * the authored layout from the persisted data without storing per-element
   * authored positions: authoredY = element.y - shiftY.
   */
  shiftY?: number;
  /** When true, elements within this row reflow horizontally: each element is
   *  positioned immediately after the previous one (left-to-right), so when one
   *  element's width changes the neighbours shift accordingly. */
  horizontalReflow?: boolean;
  /** Per-element x-shifts applied by horizontal reflow, keyed by uuid (as
   *  string for JSON compatibility). Recoverable authored x = element.x -
   *  elementShiftsX[uuid]. */
  elementShiftsX?: Record<string, number>;
  /** Designer-authored gap (in mm) to KEEP before each element when packing
   *  horizontally, keyed by uuid. Captured once when horizontal reflow is
   *  enabled, from the imported layout: gap = thisAuthoredX - (prevAuthoredX +
   *  prevContentWidth). The first element in the row has no entry. Without it
   *  the elements pack flush and the imported spacing is lost. */
  elementGaps?: Record<string, number>;
  /** Difference between the saved layout width and measured frame width, in mm. */
  elementWidthOffsets?: Record<string, { value: number; style: string }>;
  /** Element uuids in this row that act as separators ("dividers", e.g. a "|"
   *  between Mobile and Phone). A divider is shown only when BOTH the nearest
   *  non-divider neighbours around it (in visual order) are present & non-empty;
   *  otherwise it is hidden (visibility only — the element is kept). Meaningful
   *  for horizontalReflow rows. */
  dividerUuids?: number[];
  /** Horizontal alignment of the visible content within the row's authored span.
   *  Undefined means left (legacy behaviour: pack from the leftmost authored x).
   *  Only meaningful together with `horizontalReflow`. */
  horizontalAlign?: 'left' | 'center' | 'right';
  /** Authored span width of the row in mm: max(authoredX + packWidth) -
   *  min(authoredX) over ALL members, as if all were visible. Captured when an
   *  alignment is set and when horizontal reflow is enabled. It is relative to
   *  the row's anchor (leftmost authored x), so dragging the row keeps working
   *  and content width changes don't move the centre. */
  alignWidth?: number;
}

export interface ReflowGroup {
  id: string;
  name: string;
  rows: ReflowRow[];
}

/** Slots in one row of a product layout: a row holds one wide block or two or three narrower ones. */
export type SlotFormat = 1 | 2 | 3;

/** One position a product can take on the page. Shared by every count whose rows place the same
 *  product in the same rect, so a frame shown for counts 1–4 is authored once. */
export interface SlotFrame extends Rect {
  id: string;
  product: number;
  columns: SlotFormat;
  counts: number[];
}

export interface SlotFileSetFolder {
  folderId: string;
  assetIds?: string[];
}

/** A set of pictures with one folder per slot format, e.g. a machine's wide and square PDF pages. */
export interface SlotFileSet {
  id: string;
  name: string;
  folders: Partial<Record<SlotFormat, SlotFileSetFolder>>;
  /** Labels of the per-product options the pictures show under; all when unset. */
  options?: string[];
}

/** What the author fills in before a layout exists, and what an edit changes afterwards. */
export interface SlotLayoutDraft {
  name: string;
  productLabel: string;
  maxProducts: number;
  /** Per product count (index + 1), the slot columns of each row top-down; 0 leaves the row empty. */
  rows: number[][];
  area: Rect;
  gap: number;
  fileSets: SlotFileSet[];
  productOptions: string[];
}

/**
 * A page whose slots the buyer fills with 1–N products, each count having its own arrangement
 * of rows. The layout owns the count picklist, one picklist per product for the per-slot choice,
 * and the frames; the layers inside the frames are ordinary elements tagged with `slotFrame`.
 */
export interface SlotLayout extends SlotLayoutDraft {
  id: string;
  conditionId: string;
  productConditionIds: string[];
  frames: SlotFrame[];
  /** The media box the frames were laid out on, kept through reflection as a layer keeps its own. */
  pageOrigin?: {
    width: number;
    height: number;
  };
}

export interface SlotFrameRef {
  layoutId: string;
  frameId: string;
  /** The template layer this one was copied from, for a layer the author did not place by hand. */
  sourceUuid?: number;
  /** The picture set this layer draws its folder from. */
  fileSetId?: string;
}

export interface Spread {
  x: number;
  y: number;
  pages: Page[];
  tags?: string[];
  name: string;
  isTemplate?: boolean;
  canAddAfter?: boolean;
  canRemove?: boolean;
  layoutSchemaName?: string;
  sceneId?: string;
  conditionGroup?: ConditionGroup;
  reflowGroups?: ReflowGroup[];
  slotLayouts?: SlotLayout[];
  technology?: PrintTechnology;
  spot_finishing_type?: SpotFinishingType;
  white_underbase?: boolean;
}

export interface PageCountLimit {
  min: number;
  max: number;
  increment: number;
}

export interface Page {
  spread: string;
  x: number;
  y: number;
  width: number;
  height: number;
  page_nr: number;
  groups: {
    bleed?: Bleed[];
    safe_area?: SafeArea[];
    mediabox?: MediaBox[];
    content?: Content[];
    media?: MediaElement[];
    diecut?: Diecut[];
    foldingline?: FoldingLine[];
    blank?: Blank[];
    hole?: Hole[];
    barcode?: Barcode[];
  };
  backgroundColor?: string;
}

export interface ElementSize {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface GroupElement extends ElementSize {
  type: string;
  name: string;
  extra?: any;
}

export interface Bleed extends GroupElement {
  type: 'border' | 'area';
  path?: string;
  thickness?: number;
}

export interface SafeArea extends GroupElement {
  type: 'area';
  path?: string;
}

export interface Barcode extends GroupElement {
  type: 'barcode';
  path?: string;
  x: number;
  y: number;
}

export interface Hole extends GroupElement {
  type: 'circle';
  x: number;
  y: number;
  diameter: number;
}

export interface Diecut extends GroupElement {
  type: 'area';
  path: string;
  sx: number;
  sy: number;
}

export interface MediaBox extends GroupElement {
  type: 'rect';
}

export type MirroringMode = 'mirror' | 'blur';

export interface Content extends GroupElement {
  type: 'sample' | 'area' | 'panel';
  margin?:
    | {
        left: number;
        right: number;
        top: number;
        bottom: number;
      }
    | number;
  title?: string;
  description?: string[];
  rotate?: number;
  extra?: {
    mirroring?: MirroringMode;
  };
  path?: string;
}

export interface FoldingLine {
  type: 'line';
  name: string;
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

export type Blank = BlankArea;

export interface BlankCommon {
  extra: any;
  name: string;
}
export type BlankArea = BlankCommon & GroupArea;

export interface GroupLine {
  type: 'line';
  x1: number;
  x2: number;
  y1: number;
  y2: number;
}

export interface GroupArea {
  type: 'area';
  path: 'string';
  width: number;
  height: number;
  x: number;
  y: number;
}

export type MediaElement = MediaAddon | MediaImage | MediaMockupPlaceholder | MediaText | MediaGrid | MediaShape;
export type MediaElementFromType<T extends MediaElement['type']> = T extends 'addon'
  ? MediaAddon
  : T extends 'image'
    ? MediaImage
    : T extends 'text'
      ? MediaText
      : T extends 'grid'
        ? MediaGrid
        : T extends 'line'
          ? MediaLine
          : T extends 'rectangle'
            ? MediaRectangle
            : never;

type ElementOwner = { type: 'content'; pageIndex: number; contentIndex: number } | { type: 'page'; pageIndex: number };

interface MediaElementType extends GroupElement {
  group: '';
  role?: string;
  uuid: number;
  r: number;
  locked?: true;
  personalizationLocked?: true;
  sample?: true;
  hidden?: true;
  static?: true;
  unduplicable?: true;
  pageOrigin?: {
    width: number;
    height: number;
  };
  layoutFrameId?: string;
  createdByLayout?: true;
  createdWhileEmbedded?: true;
  slotFrame?: SlotFrameRef;
  belongs_to?: ElementOwner;
  stitch_count?: number;
  digitizing?: true;
  digitizing_hash?: string;
  has_spot_finishing?: true;
}

export interface PerspectiveTransform {
  tl_x: number;
  tl_y: number;
  tr_x: number;
  tr_y: number;
  bl_x: number;
  bl_y: number;
  br_x: number;
  br_y: number;
}

interface MediaImageBase extends MediaElementType {
  px: number;
  py: number;
  pw: number;
  ph: number;
  pr: number;
  flipX?: true;
  flipY?: true;
  colorOverrides?: ImageBrushData;
  embroideryScale?: number; // Scale factor for embroidery images, starts at 1
  cropOrigin?: CropOrigin;
  perspective_transform?: PerspectiveTransform;
  uploading?: true; // true when the asset is being uploaded or imported
}

// QR code personalization (GCIN-3610) — the seller marks an image layer as a QR code
// placeholder, picks a content type (URL, contact card, email, phone, SMS, WiFi, text)
// and maps each of that type's fields to a data source: either an existing
// personalizable text element on the design (the QR then updates live whenever the
// buyer edits that field) or a static seller-entered value.
// Kept as a plain string union (not imported from lib/qr/types) so this store module
// stays framework/library-agnostic — lib/qr may import from here, not the other way round.
export type QrCodeContentKind = 'url' | 'text' | 'email' | 'phone' | 'sms' | 'wifi' | 'vcard';

/** vCard-specific field keys — kept named/exported because vcardFields.ts special-cases them. */
export type QrVcardFieldKey =
  | 'firstName'
  | 'lastName'
  | 'title'
  | 'organization'
  | 'phoneCell'
  | 'phoneWork'
  | 'email'
  | 'url'
  | 'linkedin'
  | 'address';

export type QrFieldSource =
  | { type: 'element'; elementId: number } // uuid of a personalizable text element
  | { type: 'static'; value: string } // fixed value, same on every card
  | { type: 'column'; column: string }; // a code-set column — one value per printed unit

export interface QrCodeFieldConfig {
  key: string; // field key, defined per content type in lib/qr/qrFieldRegistry
  source: QrFieldSource;
  telType?: string; // vCard TEL TYPE override for phone fields (e.g. CELL, WORK, HOME, FAX)
}

export interface MediaImageQrCode {
  type: QrCodeContentKind;
  fields: QrCodeFieldConfig[]; // only fields the seller mapped to a source
  encoded?: string; // payload baked into the current imageId (skip regen when unchanged)
}

/**
 * GAPI-61890: optional shape the image is clipped to, inside its frame. Absent = plain rectangular frame.
 * The shape is inscribed in the frame (width x height), so it follows move/resize/rotate of the frame and
 * is independent from the picture crop (px/py/pw/ph). Rendered by preflight as an alpha mask.
 */
export type ImageMaskShape = 'ellipse' | 'rounded-rect' | 'circle' | 'heart' | 'arch';

export interface ImageMask {
  shape: ImageMaskShape;
  /** corner radius in mm, only for 'rounded-rect'; clamped to half of the shorter frame side */
  radius?: number;
}

export interface MediaImage extends MediaImageBase {
  type: 'image';
  mask?: ImageMask;
  /** GAPI-61891: soft inward edge of the mask (or of the frame without a mask), in mm. Absent / 0 = hard edge. */
  feather?: number;
  url?: string;
  imageId: string;
  sourceAssetId?: string; // the asset id of the original image if it was generated from it
  adjustments?: Adjustment[];
  filters?: Filter[];
  pattern?: ImagePattern;
  plugins?: any;
  linkId?: string;
  minDimensions?: Dimensions;
  sourceFrame?: Dimensions;
  shadow?: DropShadow | LeaningShadow;
  loading?: true; // true when the asset is in loading state or waiting for some changes
  qrCode?: MediaImageQrCode; // vCard QR placeholder config (web-to-print stores only)
  /**
   * GAPI-61889: per-image opacity, 0 (fully transparent) to 1 (opaque). Absent means 1; the editor never
   * stores 1, so designs that never touched the control keep their exact shape. Preflight multiplies the
   * image's alpha by it when rendering.
   */
  opacity?: number;
}

export interface MediaAddon extends MediaImageBase {
  type: 'addon';
  // Tells PreFlight which renderer to use (SVG vs raster) — sourced from
  // the source addon catalog entry's `Addon.type` when placed.
  // Optional for backwards compatibility with designs saved before OP-39533.
  subType?: AddonType.ADDON_VECTOR | AddonType.ADDON_BITMAP;
  addonId: string;
  url: string;
}

export enum AnchoringX {
  Left = 'left',
  Center = 'center',
  Right = 'right',
}

export enum AnchoringY {
  Top = 'top',
  Middle = 'middle',
  Bottom = 'bottom',
}

export enum MockupDisplayMode {
  Hanging = 'hanging',
  Leaning = 'leaning',
}
export interface MediaMockupPlaceholder extends Omit<MediaImage, 'role'> {
  type: 'image';
  role: MockupRole.Placeholder;
  variant: MockupVariant;
  area_fit?: true;
  anchoring_x?: AnchoringX;
  anchoring_y?: AnchoringY;
  shadow?: DropShadow | LeaningShadow;
  display_mode?: MockupDisplayMode;
}

export interface ProductImage {
  url: string;
  width: number;
  height: number;
}

export type CropOrigin = {
  px: number;
  py: number;
  pw: number;
  ph: number;
  width: number;
  height: number;
};

export enum Direction {
  Horizontal = 'hor',
  Vertical = 'ver',
}

export type PatternScaleBase = 'width' | 'height';
export interface BrickPattern {
  type: 'brick';
  direction: Direction;
  scale: number;
  offset?: number;
  spacing?: number;
  mirror?: true;
  scaleBase?:
    | { type: 'element'; value: PatternScaleBase }
    | { type: 'width'; value: number }
    | { type: 'height'; value: number }
    | undefined;
}

export type ImagePattern = BrickPattern;

export interface ImageManipulationPluginData {
  imageModified: true;
}

export enum ImageManipulationPluginTypes {
  BackgroundRemoval = 'app-background-removal',
  ImageUpscale = 'app-image-upscale',
  ImageEnhancement = 'image-enhancement',
}

export interface ColorOverrides {
  previous: string;
  new: string;
}

export interface ImageBrushData {
  fill?: ColorOverrides[];
  stroke?: ColorOverrides[];
  threadColors?: string[]; // Array of all final thread colors for embroidery
  url?: string;
  urlTimestamp?: number;
}

export interface Adjustment {
  name: string;
  value: number;
  custom?: boolean;
}

export interface Filter {
  name: string;
  value: number;
}

export interface MediaGridInfo {
  designName: string;
  locale: string;
  gridDate: GridDate;
  firstDayOfWeek?: number;
  hideWeekNum?: true;
}

export interface MediaGrid extends MediaElementType {
  type: 'grid';
  content: string; // svg
  grid: MediaGridInfo;
  gridHash?: string;
}

export function isMediaImage(elt: MediaElement): elt is MediaImage {
  return elt.type === 'image';
}

export enum TextWrapping {
  Wrap = 'wrap',
  Fit = 'fit',
  FitOneLine = 'fit-one-line',
}

export interface TextConditionVariant {
  text: string;
  /** Optional per-option face — a Hangul label cannot render in a JP face, so
   *  a language picklist may need the font to travel with the text. Absent
   *  means the element keeps its current font. */
  fontFamily?: string;
}

export interface TextConditionVariants {
  conditionId: string;
  /** When set, the text simply shows the active option's LABEL — the "hours"
   *  case, a dropdown of "1:00 AM"…"12:00 AM" where the printed text is just
   *  whichever one was picked, nothing to author. `values` is then ignored for
   *  both text and font; it is kept rather than cleared so switching back to
   *  per-option text (see VaryingTextsSection) restores whatever was written
   *  before, instead of starting over. Absent — the default, and the only
   *  shape any design saved before this flag existed can have — is today's
   *  per-option authoring: the string comes from `values[optionId]` and an
   *  unauthored option leaves the element's text unchanged. */
  useOptionLabel?: true;
  values: { [optionId: string]: TextConditionVariant };
}

/**
 * Per-field normalisation the template forces on whatever the buyer types.
 * Applied when the value is committed, so the formatted string is what gets
 * stored, previewed and printed — see applyValueFormat.
 */
export type TextValueFormat =
  | {
      kind: 'mask';
      /**
       * Pattern the digits are poured into: every MASK_DIGIT takes the next
       * digit the buyer typed and any other character is kept as written, so
       * "+1 000 000 0000" turns 2224445555 into "+1 222 444 5555". One free
       * text pattern rather than a fixed menu, because every template phrases
       * its numbers differently.
       */
      mask: string;
    }
  | {
      kind: 'emailDomain';
      /** Domain forced onto the local part, with or without the leading "@". */
      domain: string;
    }
  | {
      kind: 'regex';
      /** Bare pattern ("[A-Z]{2}-\d{4}") or literal form ("/[a-z]+/i"). */
      pattern: string;
    };

export interface MediaText extends MediaElementType {
  type: 'text';
  sx: number;
  sy: number;
  fill: string;
  rendered_with: 'opentype.js';
  uneditable?: true;
  html?: string;
  generatedType?: LayoutGeneratedTextType;
  extra: {
    text: string;
    fontSize: number;
    fontFamily: string;
    textAlign?: TextAlign;
    lineHeight?: number;
    underline?: boolean;
    linethrough?: boolean;
    direction?: TextDirection;
    letterSpacing?: number;
    stroke?: Stroke;
    curve?: number;
    shadow?: DropShadow;
    flexibleWidth?: boolean;
    wrapping?: TextWrapping;
    /** Fitting policy recovered from a verified legacy server-generated font outline. */
    customFontLayout?: 'server-fit-v1';
    maxCharCount?: number;
    lockBoxSize?: boolean;
    /** GAPI-61892: the frame keeps its height and the text wraps at its own size inside
     *  it, like a text frame in a page-layout tool, instead of the frame growing to fit
     *  the text. Text that needs more room overflows: the canvas marks the frame and a
     *  TextOverflow warning is raised. Only applies to plain wrapping text (no autofit,
     *  lockBoxSize, curve or flexibleWidth). Absent — every design saved before — keeps
     *  today's auto-growing frame. */
    fixedFrame?: true;
    isMandatory?: boolean;
    /** Author-written guidance shown to the buyer beside the field
     *  (e.g. "Must include country code"). Shares its spot with the
     *  max-characters note rather than adding a second one. */
    hint?: string;
    /** Forces the buyer's input into the template's canonical form on commit. */
    valueFormat?: TextValueFormat;
    /** Cross-page shared value: every personalisable text carrying the same id
     *  holds one string, so Name / Title / Email are typed once and print on
     *  the front AND the back of a card. Only the string is shared — position,
     *  font, required flag and character cap stay per element, because the back
     *  of a card sets them differently from the front. */
    sharedFieldId?: string;
    /** Per-option content for a text whose string varies with a Condition
     *  (e.g. a printed label reading T / 電話 / 전화 depending on the chosen
     *  language). The element itself stays put — one member of its reflow row,
     *  one gap entry, one style — and only its text (and optionally font) is
     *  swapped when the condition's active option changes. This is what makes
     *  a per-language label viable inside a horizontal reflow row shared with
     *  language-independent fields; linking N cloned labels to N options would
     *  bloat the row and clones would pack with no gap (elementGaps is keyed
     *  by uuid and captured once). */
    textVariants?: TextConditionVariants;
    /** This text prints a code-set column, one row per unit, instead of what
     *  the buyer types. `extra.text` holds the sample row's value so the
     *  canvas and previews stay realistic; production writes the real one. */
    variableColumn?: string;
    /** GAPI-61895: id of the design text style (`DesignData.textStyles`) this
     *  text follows. Kept when the author edits a governed property directly
     *  on the text: the text is then "modified" (derived by comparing its
     *  values with the style's) until the style is updated from it or the
     *  text is reset to the style. */
    textStyleId?: string;
  };
  path?: string; // SVG path data for server-rendered protected fonts
  stitch_count?: number;
  sourceFrame?: Dimensions;
  uploading?: true; // true when the digitized version is being uploaded or imported
}

export interface Stroke {
  color: string;
  width: number;
}

export interface DropShadow {
  type: 'drop-shadow';
  offsetX: number;
  offsetY: number;
  blur: number;
  color: string;
  scale?: number;
  absolute_direction?: true;
}

export type ShadowSide = 'left' | 'right';

export interface LeaningShadow {
  type: 'leaning-shadow';
  blur: number;
  color: string;
  side?: ShadowSide;
}

export type LineEdge = 'flat' | 'arrow' | 'arrow-fill';

export type MediaShape = MediaLine | MediaRectangle;

export type DashPattern = { dash: number; gap: number };

export interface MediaShapeBase {
  strokeWidth: number;
  uuid: number;
  locked?: true;
  personalizationLocked?: true;
  unduplicable?: true;
  name: string;
  hidden?: true;
  static?: true;
  pageOrigin?: {
    width: number;
    height: number;
  };
  createdWhileEmbedded?: true;
  slotFrame?: SlotFrameRef;
  belongs_to?: ElementOwner;
  stitch_count?: number;
  digitizing?: true;
  digitizing_hash?: string;
  has_spot_finishing?: true;
}

export interface MediaLine extends MediaShapeBase {
  type: 'line';
  stroke: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  edge1: LineEdge;
  edge2: LineEdge;
  dashPattern?: DashPattern;
  rounded: boolean;
}

export interface MediaRectangle extends MediaShapeBase {
  type: 'rectangle';
  x: number;
  y: number;
  width: number;
  height: number;
  r: number;
  cornerRounding?: number;
  fill?: string;
  stroke?: string;
  dashPattern?: DashPattern;
}

export interface ElementAddress {
  spreadIndex: number;
  pageIndex: number;
  elementIndex: number;
}

export interface ContentAddress {
  spreadIndex: number;
  pageIndex: number;
  contentIndex: number;
}

export interface StructureIndex extends ElementAddress {
  uuid: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ElementRect extends Rect {
  r: number;
}

export interface ContentRect extends Rect {
  type: string;
}

export interface Coords {
  left: number;
  top: number;
}

export interface Dimensions {
  width: number;
  height: number;
}

export type SpreadGroupPosition = 'vertical' | 'horizontal' | 'center';

export interface SpreadGroup {
  spreadIndexes: number[];
  position: SpreadGroupPosition;
  name: string;
  layoutTag?: string;
}

export interface SpreadInfo {
  spreadIndex: number;
  spreadWidth: number;
  spreadHeight: number;
  spreadCoords: Coords;
}

export interface ReflectDesignDataConfig {
  resetMedia?: boolean;
  useAsSource?: boolean;
  ignore?: {
    plugins: (PluginName | string)[];
    elementTypes: (MediaElement['type'] | MediaShape['type'])[];
  };
}

export enum SpotFinishingType {
  Gold = 'foil-gold',
  Copper = 'foil-copper',
  Silver = 'foil-silver',
}

export type SavingDesignStatus = 'saving' | 'saved' | 'offline';
